/**
 * Rentals API Client
 * Epic 14: Bérlés kezelés
 */

import type {
  InlineSignStartResult,
  RentalDiscountApplyResult,
  RentalItemDiscountInput,
} from '@kgc/api-contract';
// page5 (#421) return-list seam — FE-safe dto sub-path (vite/vitest+tsconfig alias)
import type { RentalDueFilter } from '@kgc/rental-core/rental-list';
import { ApiError, api } from './client';

/**
 * ADR-137 reservation-lock — a foglaltság-CAS ütközés error-code-ja (#314).
 * A `PATCH /rentals/:id/pickup` 409-et ad `{ code: INVENTORY_CAS_CONFLICT, conflictingItems[] }`
 * alakban, ha egy másik pult közben lefoglalta a gépet.
 */
export const INVENTORY_CAS_CONFLICT = 'INVENTORY_CAS_CONFLICT';

/** True, ha a hiba a pickup-CAS foglaltság-ütközése (#314 wire-contract). */
export function isInventoryCasConflict(err: unknown): boolean {
  if (err instanceof ApiError) return err.code === INVENTORY_CAS_CONFLICT;
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { code?: unknown }).code === INVENTORY_CAS_CONFLICT
  );
}

// ============================================
// TYPES
// ============================================

export enum RentalStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  OVERDUE = 'OVERDUE',
  RETURNED = 'RETURNED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export interface RentalEquipment {
  id: string;
  name: string;
  serialNumber: string;
  condition: string;
}

export interface RentalPartner {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  type: 'INDIVIDUAL' | 'COMPANY';
}

export interface RentalItem {
  id: string;
  equipmentId: string;
  equipmentName: string;
  serialNumber: string;
  quantity: number;
  dailyRate: number;
  appliedRate: number;
  totalDays: number;
  itemTotal: number;
  depositAmount: number;
  returnedAt?: string;
  condition?: string;
}

/**
 * PV-KAUCIO-002 (32-kör, read-DTO #681): per-gép kaució-rekord.
 * A BE `Deposit.status` enum értékei: PENDING | RECEIVED | HELD | RELEASED | RETAINED | PARTIAL.
 */
export interface RentalDeposit {
  /**
   * A backend Deposit UUID — a refund/retain endpoint `depositId`-ja (#687 read-DTO contract).
   * Optional backward-compat: a #687 read-DTO még nem mergelt, így legacy/pre-merge payloadban
   * hiányozhat. NEM azonos a `rentalItemId`-vel. Ha hiányzik → nincs per-gép kaució-akció.
   */
  id?: string | undefined;
  rentalItemId?: string | undefined;
  equipmentName?: string | undefined;
  amount: number;
  refundStatus: string;
  /**
   * 40-kör (PV-BERLES-003): a gépenkénti azonnali elszámolás időpontja (ISO-string)
   * — a PR-0 `Deposit.settledAt` migration read-vetülete. Optional backward-compat:
   * amíg a settlement-core read-DTO nem adja, hiányzik → nincs Elszámolva-chip.
   */
  settledAt?: string | undefined;
  /**
   * 40-kör W2 (#746 read-DTO): a kaució fizetési módja — READ-enum UPPERCASE
   * (CASH|CARD|BANK_TRANSFER|PRE_AUTH). Kártyás (CARD/PRE_AUTH) kaución a
   * partial-refund a MyPOS-úton megy → a FE-CTA disabled (BE-422 elé honesty).
   * Optional backward-compat a #746 merge-éig.
   */
  paymentMethod?: string | undefined;
}

export interface Rental {
  id: string;
  rentalCode: string;
  status: RentalStatus;
  customerId: string;
  customerName: string;
  equipmentId: string;
  equipmentName: string;
  startDate: string;
  expectedReturnDate: string;
  actualReturnDate?: string;
  dailyRate: number;
  totalAmount: number;
  depositAmount: number;
  depositPaid: number;
  depositReturned: number;
  lateFeeAmount: number;
  notes?: string | undefined;
  items: RentalItem[];
  createdAt: string;
  updatedAt: string;
  /** Calculated outstanding debt: daysOverdue × totalDailyRate */
  currentDebt?: number | undefined;
  /** PV-BERLES-001 (31-kör, read-DTO #679): befizetett bérleti díj (HUF). */
  paidAmount?: number | undefined;
  /** PV-BERLES-001: hűségpont-kedvezmény (HUF). */
  loyaltyRedeemAmount?: number | undefined;
  /** PV-BERLES-001: fennálló egyenleg (COMPUTED a BE-ben: grandTotal − loyaltyRedeemAmount − paidAmount). */
  outstanding?: number | undefined;
  /**
   * A kapcsolódó RentalContract aláírásának időpontja (ISO-string) vagy undefined
   * (BE-DTO top-level, #322). Az inline-sign (ADR-103 Am-6) állítja be; a re-sign
   * guardhoz a DRAFT „Aláírás most" CTA-t erre gate-eljük (B1).
   */
  contractSignedAt?: string | undefined;
  /** PV-KAUCIO-002 (32-kör, read-DTO #681): per-gép kaució-bontás. List-ben üres (detail-only). */
  deposits?: RentalDeposit[] | undefined;
  /**
   * 39-kör FE-4 (#739 read-expozíció): késedelmi-díj bontás (LateFeeDetails) —
   * CSAK a rental.controller útvonalain jön (PATCH :id/return válasza, GET /rentals/:id),
   * a /rentals-direct lista/detail NEM adja. `unknown`: a fogyasztó a
   * getLateFeeBreakdownView safeParse-gate-jén át olvassa (#718-guard).
   */
  lateFee?: unknown;
  /**
   * 41-kör FE-2 (#755 read-pin, rentals-direct): a MOSTANI visszahozásnál várható
   * késedelmi díj a 39-es SSoT-ból (calculateDailyLateFee — a visszavételkor
   * ugyanez a formula terhel). CSAK lejárt, kint lévő bérlésen jön; a mező
   * HIÁNYA = nincs díj. ELŐNÉZET — nem tárolt összeg. 0 FE-számítás: a
   * lista-oszlop/kártya közvetlenül ezt rendereli.
   */
  estimatedLateFee?: number | undefined;
}

export interface RentalListResponse {
  data: Rental[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    hasMore: boolean;
  };
}

export interface RentalResponse {
  data: Rental;
}

export interface RentalStatistics {
  totalActive: number;
  totalOverdue: number;
  totalRevenue: number;
  averageRentalDuration: number;
}

export interface RentalFilters {
  status?: RentalStatus;
  customerId?: string;
  equipmentId?: string;
  overdueOnly?: boolean;
  search?: string;
  page?: number;
  pageSize?: number;
  /** page5 (#421): szerver-oldali due-ablak szűrő (OVERDUE | DUE_TODAY | DUE_SOON). */
  dueFilter?: RentalDueFilter;
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Get list of rentals with filters
 * Uses /rentals-direct endpoint which directly queries Prisma
 */
export async function getRentals(filters: RentalFilters = {}): Promise<RentalListResponse> {
  const params = new URLSearchParams();

  if (filters.status) params.append('status', filters.status);
  if (filters.customerId) params.append('customerId', filters.customerId);
  if (filters.equipmentId) params.append('equipmentId', filters.equipmentId);
  if (filters.overdueOnly) params.append('overdueOnly', 'true');
  if (filters.dueFilter) params.append('dueFilter', filters.dueFilter);
  if (filters.search) params.append('search', filters.search);
  // M-018 fix: Use !== undefined to avoid falsy-zero bug
  if (filters.page !== undefined) params.append('page', filters.page.toString());
  if (filters.pageSize !== undefined) params.append('pageSize', filters.pageSize.toString());

  const queryString = params.toString();
  const endpoint = queryString ? `/rentals-direct?${queryString}` : '/rentals-direct';

  return api.get<RentalListResponse>(endpoint);
}

/**
 * Get single rental by ID
 * Uses /rentals-direct endpoint which directly queries Prisma
 */
export async function getRentalById(id: string): Promise<RentalResponse> {
  return api.get<RentalResponse>(`/rentals-direct/${id}`);
}

/**
 * Get rental statistics
 */
export async function getRentalStatistics(): Promise<{ data: RentalStatistics }> {
  return api.get<{ data: RentalStatistics }>('/rentals/statistics');
}

/**
 * Rental expiration notification — a `GET /rental-expirations` wire-projekciója.
 * A `@kgc/rental-core` `RentalExpirationNotification`-t tükrözi FE-oldalon (a rental-core
 * nem kgc-web dependency + a service-nek nincs FE-safe sub-path alias-a). A dátum-mezők a
 * wire-en ISO-stringek (a BE-interface `Date`-je JSON-serializáláskor stringgé válik —
 * #701 Date-drift-osztály), ezért string-ként tükrözve.
 */
export interface RentalExpiration {
  rentalId: string;
  level: 'INFO' | 'WARNING' | 'URGENT';
  daysUntilExpiry: number;
  isOverdue: boolean;
  partnerName: string;
  partnerPhone?: string | undefined;
  equipmentName: string;
  expiryDate: string;
  assignedUserId?: string | undefined;
}

/**
 * GET /rental-expirations — a lejáró/lejárt bérlések „kit hívjak" értesítés-listája
 * (`RentalExpirationController`). A wire `{ data: RentalExpiration[] }` alakú
 * (#718-konzisztencia-wrap). rentalId-vel join-olható az OverdueRentalsPage soraira.
 */
export async function getRentalExpirations(): Promise<RentalExpiration[]> {
  // #718-wrap tranzíció-guard: a controller a `{ data: [] }`-wrapre áll át (Zeno BE-PR),
  // addig direkt tömböt ad. Mindkét alak biztonságos — a köztes merge-ablakban sem crashel
  // az OverdueRentalsPage (a direkt-tömb és a {data}-wrap is helyesen unwrappel), így a PR
  // a wrap-PR-től FÜGGETLENÜL mergelhető (#727-minta: graceful köztes-ablak).
  const response = await api.get<{ data: RentalExpiration[] } | RentalExpiration[]>(
    '/rental-expirations'
  );
  return Array.isArray(response) ? response : (response.data ?? []);
}

/**
 * Create new rental
 */
export async function createRental(data: {
  partnerId: string;
  items: Array<{ inventoryItemId: string; quantity: number }>;
  startDate: string;
  plannedEndDate: string;
  depositPaymentMethod: 'cash' | 'card' | 'mypos_preauth' | 'mypos_sale' | 'bank_transfer';
  notes?: string;
  additionalItems?: Array<{ productId: string; quantity: number; unitPrice: number }>;
}): Promise<RentalResponse> {
  return api.post<RentalResponse>('/rentals', data);
}

/**
 * Confirm equipment pickup
 */
export async function confirmPickup(
  id: string,
  data: {
    accessoryChecklistVerified: boolean;
    depositCollected: boolean;
    // Q-T138: a BE `depositMethod`-enum (DepositPaymentMethod) kisbetűs token —
    // az uppercase-t a globális ValidationPipe @IsEnum-je 400-zal elutasítja.
    // (`PRE_AUTH` → `mypos_preauth`; a mypos_sale/bank_transfer a pickupon nem használt.)
    depositMethod?: 'cash' | 'card' | 'mypos_preauth';
    notes?: string;
  }
): Promise<RentalResponse> {
  return api.patch<RentalResponse>(`/rentals/${id}/pickup`, data);
}

/**
 * Process equipment return
 */
export async function processReturn(
  id: string,
  data: {
    returnDate: string;
    // M4 expanded return conditions — a BE ProcessReturnDto 8-értékű enumja a kanonikus
    condition:
      | 'EXCELLENT'
      | 'GOOD'
      | 'FAIR'
      | 'POOR'
      | 'DAMAGED'
      | 'DAMAGED_USABLE'
      | 'DAMAGED_SERVICE'
      | 'INCOMPLETE';
    damageNotes?: string;
    depositAction?: 'RETURN' | 'RETAIN_PARTIAL' | 'RETAIN_FULL';
    retainedAmount?: number;
    retentionReason?: string;
    /**
     * 40-kör W1 (#746, PV-BERLES-003 b-ratify): gépenkénti elszámolás-mód.
     * Elhagyva = 'NOW' (default). AT_CLOSE = halasztás a bérlés zárásáig
     * (settledAt NULL marad); a záró-gép visszavételekor a BE zárás-invariánsa
     * MINDEN elszámolatlan depositot atomian rendez a választástól függetlenül.
     */
    settleMode?: 'NOW' | 'AT_CLOSE';
    /** W1: gépenkénti részleges visszavétel item-uuid-jai; elhagyva = minden item (legacy). */
    returnedItemIds?: string[];
  }
): Promise<RentalResponse> {
  return api.patch<RentalResponse>(`/rentals/${id}/return`, data);
}

/**
 * Extend rental period
 */
export async function extendRental(
  id: string,
  data: {
    newReturnDate: string;
    reason?: string;
    legalAccepted?: boolean;
  }
): Promise<RentalResponse> {
  return api.patch<RentalResponse>(`/rentals/${id}/extend`, data);
}

/**
 * Cancel rental
 */
export async function cancelRental(id: string, reason: string): Promise<RentalResponse> {
  return api.post<RentalResponse>(`/rentals/${id}/cancel`, { reason });
}

/**
 * ADR-037 Am-1 — per-gép manuális engedmény alkalmazása (post-creation, DRAFT/CONFIRMED).
 * A BE role-cap-clamp + flag (`cappedByRole`); a #310 `RentalDiscountApplyResult` 1:1.
 * Endpoint: `POST /rentals/:id/item-discounts` (PR #312).
 */
export async function applyItemDiscounts(
  rentalId: string,
  items: RentalItemDiscountInput[]
): Promise<{ data: RentalDiscountApplyResult }> {
  return api.post<{ data: RentalDiscountApplyResult }>(`/rentals/${rentalId}/item-discounts`, {
    items,
  });
}

/**
 * ADR-103 Am-6 — inline-sign 1. lépés (X'-flow, #316): thin get-or-create contract a
 * rentalId-n. A 2. lépés (`createSigningSession`) + 3. (`submitSignature`) a meglévő
 * ADR-099 esign-stacken megy (a SignContractDialog kezeli). Endpoint: `POST /contracts/inline-sign/start`.
 */
export async function inlineSignStart(
  rentalId: string,
  idempotencyKey: string,
  templateId?: string
): Promise<{ data: InlineSignStartResult }> {
  return api.post<{ data: InlineSignStartResult }>(`/contracts/inline-sign/start`, {
    rentalId,
    idempotencyKey,
    ...(templateId ? { templateId } : {}),
  });
}

// ============================================
// PV-BERLES-001: Részleges-fizetés rögzítése (31-kör FE-1)
// ============================================

/** A PATCH /rentals/:id/payment request body (BE RecordPaymentDtoSchema 1:1). */
export interface RecordRentalPaymentDto {
  amount: number;
  paymentMethod: 'CASH' | 'CARD' | 'TRANSFER';
  paymentReference?: string | undefined;
  notes?: string | undefined;
}

/**
 * A PATCH /rentals/:id/payment válasza: a domain Rental + COMPUTED outstanding.
 */
export interface RecordRentalPaymentResponse {
  data: {
    outstanding: number;
    paidAmount: number;
    loyaltyRedeemAmount: number;
  };
}

export const RENTAL_OVERPAYMENT = 'RENTAL_OVERPAYMENT';

export async function recordRentalPayment(
  id: string,
  dto: RecordRentalPaymentDto
): Promise<RecordRentalPaymentResponse> {
  return api.patch<RecordRentalPaymentResponse>(`/rentals/${id}/payment`, dto);
}

export function rentalPaymentErrorToastKey(error: unknown): string {
  const code = error instanceof ApiError ? error.code : undefined;
  switch (code) {
    case RENTAL_OVERPAYMENT:
      return 'rental.payment.overpaymentError';
    default:
      return 'rental.payment.recordFailed';
  }
}

/**
 * L1(b) Partner-46 — TRANSFER fizetési-mód gate deny-kódjai (BE-kontraktus tükre:
 * `TransferPaymentGateService.TransferGateResult`). A `PATCH /rentals/:id/payment` 400-at ad
 * `{ code, message }` alakban, ha átutalásnál a számlázott cég nem-listázott személynek fizet,
 * le van tiltva (Három Csapás), vagy a cég-oldal magát a személyt tiltotta le (#832 d-tétel).
 */
export type TransferGateDenyCode =
  | 'TRANSFER_BANNED'
  | 'TRANSFER_NOT_ELIGIBLE'
  | 'TRANSFER_PERSON_BLOCKED';

/**
 * A fizetés-hibából a TRANSFER-gate elutasítást nyeri ki (code + BE reason), különben null.
 * Reaktív ág (A): a `recordRentalPayment` 400-catch-éből. A későbbi proaktív ág (B, READ-only
 * `GET /rentals/:id/transfer-eligibility`) ugyanezt a `{ code, reason }` alakot adja — a fogyasztó
 * (`TransferEligibilityNotice`) forrás-agnosztikus.
 */
export function rentalTransferGateDenial(
  error: unknown
): { code: TransferGateDenyCode; reason: string } | null {
  if (!(error instanceof ApiError)) return null;
  if (
    error.code === 'TRANSFER_BANNED' ||
    error.code === 'TRANSFER_NOT_ELIGIBLE' ||
    error.code === 'TRANSFER_PERSON_BLOCKED'
  ) {
    return { code: error.code, reason: error.message };
  }
  return null;
}

// ── Settlement-preview (40-kör PV-BERLES-003, PR-5 pin) ──

export interface SettlementPreviewItemInput {
  rentalItemId: string;
  /** Kár (pultos-input), egész Ft */
  damageAmount?: number | undefined;
  /** Elveszett tartozék — BESZERZÉSI ár, egész Ft */
  missingAccessoryCharge?: number | undefined;
  /** Tisztítás-override (nincs → BE-default), egész Ft */
  cleaningFeeOverride?: number | undefined;
}

/**
 * Read-only settlement-előnézet kalkuláció (0 DB-write) a pultos inputjaira.
 * A válasz nyers `data`-ját a SettlementPreviewWireSchema safeParse-gate-je
 * validálja a fogyasztóban (#718-guard) — amíg a PR-5 endpoint nem él,
 * a hívás hibája graceful no-panel.
 */
export async function previewSettlement(
  id: string,
  items: SettlementPreviewItemInput[]
): Promise<{ data: unknown }> {
  return api.post<{ data: unknown }>(`/rentals-direct/${id}/settlement-preview`, { items });
}
